/**
 * Loading state for BillingPage.
 *
 * Deliberately mirrors BillingPage's real structure — same `space-y-6` stack,
 * same section headings, same card surfaces with the same padding — so the
 * swap to loaded content moves as little as possible. A skeleton whose shape
 * doesn't match the content it replaces just relocates the layout shift; the
 * point is to reserve the actual geometry. The surface and label classes come
 * from ./styles so the two files cannot drift apart.
 *
 * The static chrome (page title, section headings) is rendered for real rather
 * than as grey blocks: it is not data, it never changes, and drawing it
 * truthfully means those pixels are already final when the data lands.
 *
 * One block is deliberately NOT mirrored: the prepaid-credit card. Whether it
 * renders at all depends on the response (a balance, or a provisioned SKU
 * catalog), so reserving its height would guarantee a shift on every install
 * that has neither — a bigger jump than the one it would prevent. Reserve
 * geometry only where the content is unconditional.
 */
import { Skeleton } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { CARD, SECTION_LABEL } from './styles.js'

/** Matches the plan-row count we typically render; three keeps the reserved
 *  height close to the real list without guessing at the catalog. */
const PLAN_ROWS = 3

export function BillingSkeleton({ canManageBilling }: { canManageBilling: boolean }) {
  const { t } = useTranslation('billing')

  return (
    // aria-busy is the accessible signal here — the blocks themselves are
    // aria-hidden, so a screen reader hears "busy", not a wall of empty divs.
    <div className="space-y-6" aria-busy="true" aria-live="polite" aria-label={t('loading')}>
      {/* Header — real text, since it's static chrome that won't move. */}
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-lg themed-text font-medium">{t('title')}</h2>
          <p className="text-xs themed-text-muted mt-0.5">{t('subtitle')}</p>
        </div>
        <Skeleton className="h-4 w-16" />
      </div>

      {/* Current plan — mirrors the hero grid: plan card (2 cols) beside the
          billable-seats stat card. */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className={`${CARD} lg:col-span-2 p-4 sm:p-6 space-y-5`}>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2.5">
              <Skeleton className="h-2.5 w-20" />
              <Skeleton className="h-9 w-40" />
            </div>
            <div className="space-y-1.5 flex flex-col items-end">
              <Skeleton className="h-9 w-28" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
          {/* Budget meter + overage — mirrors the real hero (h-2 track). */}
          <div className="pt-5 border-t themed-border space-y-3">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Skeleton className="h-3.5 w-32" />
                <Skeleton className="h-3.5 w-20" />
              </div>
              <Skeleton className="h-2 w-full rounded-full" />
              <Skeleton className="h-2.5 w-24" />
            </div>
            <div className="flex items-center justify-between pt-3 border-t themed-border">
              <Skeleton className="h-3.5 w-24" />
              <Skeleton className="h-3.5 w-16" />
            </div>
            <Skeleton className="h-2.5 w-3/4" />
          </div>
        </div>
        <div className={`${CARD} p-6 flex flex-col items-center justify-center text-center gap-4`}>
          <Skeleton className="h-14 w-14 rounded-2xl" />
          <div className="space-y-2 flex flex-col items-center">
            <Skeleton className="h-2.5 w-24" />
            <Skeleton className="h-10 w-12" />
          </div>
          {/* Add-seats button (org admins only — matches the real card). */}
          {canManageBilling && <Skeleton className="h-10 w-full rounded-xl" />}
        </div>
      </section>

      {/* Plan picker — owner/admin only, same gate as the real page so a member
          isn't shown a placeholder for a section they'll never receive. The
          overage/usage now lives inside the hero, so there's no separate card
          here anymore. */}
      {canManageBilling && (
        <section>
          <div>
            <h3 className={`${SECTION_LABEL} block`}>{t('planPicker.heading')}</h3>
            <p className="text-sm themed-text-muted mt-1">{t('planPicker.subheading')}</p>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {Array.from({ length: PLAN_ROWS }, (_, i) => (
              <li key={i} className={`${CARD} flex flex-col p-4`}>
                <Skeleton className="h-5 w-24" />
                {/* tagline (min-h-10 in the real card) */}
                <Skeleton className="h-3 w-40 mt-2" />
                {/* price */}
                <Skeleton className="h-7 w-28 mt-4" />
                {/* included-budget eyebrow */}
                <Skeleton className="h-2.5 w-32 mt-3" />
                {/* feature bullets */}
                <div className="space-y-3 mt-6 mb-7 flex-1">
                  <Skeleton className="h-3 w-36" />
                  <Skeleton className="h-3 w-28" />
                  <Skeleton className="h-3 w-32" />
                </div>
                <Skeleton className="h-11 w-full rounded-xl" />
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Usage credits — rendered for EVERYONE, matching the real page: the
          balance is member-visible, only the buy/settings controls are gated.
          The heading is real text (static chrome); the blocks below reserve the
          balance figure and the buy row that follow it. */}
      <section className="space-y-3">
        <h3 className={SECTION_LABEL}>{t('credits.heading')}</h3>
        <div className={`${CARD} p-4 sm:p-6 space-y-5`}>
          <div className="space-y-2">
            <Skeleton className="h-2.5 w-24" />
            <Skeleton className="h-9 w-32" />
            <Skeleton className="h-3 w-64" />
          </div>
          {canManageBilling && (
            <div className="pt-5 border-t themed-border space-y-3">
              <Skeleton className="h-3.5 w-24" />
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                {Array.from({ length: 5 }, (_, i) => (
                  <Skeleton key={i} className="h-16 w-full rounded-xl" />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
